import { classNames } from "@components/library/utils/classNames";
import { isNil } from "lodash";
import { formatNumberPercentage } from "@/lib/utils";
import { CaretDown, CaretUp } from "@phosphor-icons/react";

type ChangeDirection = "improved" | "regressed" | "unchanged";

interface Props {
  value: number;
  comparison: number | null | undefined;
  higherIsBetter?: boolean;
  onClick?: () => void;
}

/** Displays the relative delta between two values.
 *
 * Displays a percentage and a directional arrow indicating whether the value has improved or regressed.
 */
export const Delta = ({ comparison, value, higherIsBetter, onClick }: Props) => {
  let changeDirection: ChangeDirection | null = null;
  let deltaPercentage = 0;
  if (!isNil(comparison)) {
    changeDirection =
      value === comparison
        ? "unchanged"
        : higherIsBetter !== undefined
          ? higherIsBetter === value > comparison
            ? "improved"
            : "regressed"
          : "unchanged";
    deltaPercentage = Math.abs((value - comparison) / comparison);
  }

  return (
    !isNil(comparison) && (
      <div
        onClick={onClick}
        className={classNames(
          "flex h-20 items-center gap-4 truncate rounded px-6 py-4 text-12-12 font-normal focus:ring-0",
          !isNil(onClick) && "hover:cursor-pointer",
          changeDirection === "unchanged" &&
            classNames("bg-background-base-1 text-text-base-3", !isNil(onClick) && "hover:bg-background-base-2"),
          changeDirection === "improved" &&
            classNames("bg-background-success-1 text-text-success-2", !isNil(onClick) && "hover:bg-green-150"),
          changeDirection === "regressed" &&
            classNames("bg-background-danger-1 text-text-danger-2", !isNil(onClick) && "hover:bg-red-150"),
        )}
      >
        <div
          className={classNames(
            "flex w-full items-center gap-4 rounded-full",
            changeDirection === "improved" && "opacity-80", // Opacity to make it lighter on a white background, to make it more color-blind friendly
          )}
        >
          {comparison > value ? <CaretDown size={10} weight="fill" /> : <CaretUp size={10} weight="fill" />}

          {formatNumberPercentage(deltaPercentage, {
            // EITHER significant digits or fraction digits are used by IntlNumberFormatter, not both
            maximumSignificantDigits: deltaPercentage > 1000 ? 4 : undefined,
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}
        </div>
      </div>
    )
  );
};
